import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, RefreshCw, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';

const RefundPolicy = () => {
  const sections = [
    {
      title: 'No Refund on Digital Purchases',
      content: 'All digital purchases—whether subscriptions, or individual point purchases—are non-refundable once added to your account. This includes accidental purchases, change of mind, or unused points. Please review your selection carefully before confirming payment.'
    },
    {
      title: 'Refund Eligibility (Exceptional Cases)',
      content: 'Although all sales are final, refunds may be approved only under exceptional circumstances, such as: You were charged multiple times for the same transaction; A technical issue occurred (e.g., points deducted but not credited) and couldn\'t be resolved by our support team; The purchased content was unavailable or significantly different from what was advertised. Important: Refund requests must be submitted within 48 hours of the transaction to be considered.'
    },
    {
      title: 'How to Request a Refund',
      content: 'If your case meets the above conditions, please email us at support@smronline.org with: Your registered email or phone number, Transaction ID and a screenshot of payment, A short description of the issue. Our team will review your request and respond within 5 business days. If approved, refunds will be credited to your original payment method within 7–10 working days.'
    },
    {
      title: 'In-App Coin Purchases',
      content: 'Points purchased within the app are non-refundable once used. Unused points remain in your wallet and do not expire. You can use them for future participation.'
    },
    {
      title: 'Subscriptions via App Stores',
      content: 'If you subscribed through Google Play or the Apple App Store, all refund requests must be made directly through the respective store\'s refund process. SMR Online cannot process refunds for purchases made via third-party platforms.'
    },
    {
      title: 'Subscription Upgrades and Downgrades',
      content: 'If you upgrade your plan (e.g., from monthly to yearly), you may request a downgrade within 30 days of purchase. Your plan will be adjusted to the lower tier and the balance will be credited to your SMR Online wallet as non-withdrawable credits for future purchases. Note: Downgrade requests after 30 days will not be accepted.'
    },
    {
      title: 'Auto-Renewal Policy',
      content: 'All SMR Online subscriptions (if applicable) renew automatically by default. To avoid renewal charges, please cancel your subscription before the renewal date through your SMR Online Account Settings, or Google Play / Apple App Store.'
    },
    {
      title: 'Contact Us',
      content: 'For any questions about refunds, purchases, or subscriptions, please contact us at: Company Name: SMR Online, Email: support@smronline.org'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-green-50">
      {/* Header */}
      <motion.header
        className="bg-white/80 backdrop-blur-sm border-b border-gray-100 pt-20"
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <div className="w-full max-w-[85%] mx-auto px-4 md:px-6 py-4 md:py-6">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3 group">
              <ArrowLeft className="w-5 h-5 text-primary group-hover:text-primary-dark transition-colors duration-300" />
              <span className="text-primary group-hover:text-primary-dark transition-colors duration-300">Back to Home</span>
            </Link>
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-primary rounded-xl flex items-center justify-center">
                <RefreshCw className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-gradient">
                Refund Policy
              </h1>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Main Content */}
      <main className="w-full max-w-[85%] mx-auto px-4 md:px-6 py-8 md:py-12">
        {/* Hero Section */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <h1 className="text-4xl lg:text-6xl font-bold mb-6 text-gray-900">
            Refund &{' '}
            <span className="text-gradient">Returns Policy</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            SMR Online Refund and Returns Policy
          </p>
          <p className="text-sm text-gray-500 mt-4">
            Effective Date: 11th October 2025 | Company: SMR Online
          </p>
        </motion.div>

        {/* Intro */}
        <motion.p
          className="text-gray-600 text-lg leading-relaxed mb-8 text-center max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          Welcome to SMR Online, India's leading Matka platform. This Refund and Returns Policy explains
          how refunds, subscriptions, and digital purchases are managed on SMR Online. By accessing or
          using our platform, you agree to the following terms.
        </motion.p>

        {/* Policy Content */}
        <motion.div
          className="bg-white rounded-3xl p-8 lg:p-12 shadow-lg border border-gray-100 space-y-8"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          {sections.map((section, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="flex items-start gap-4">
                <span className="flex-shrink-0 w-8 h-8 bg-gradient-primary rounded-lg flex items-center justify-center text-white font-bold text-sm">
                  {index + 1}
                </span>
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">
                    {section.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {section.content}
                  </p>
                </div>
              </div>
              {index < sections.length - 1 && (
                <hr className="mt-8 border-gray-100" />
              )}
            </motion.div>
          ))}
        </motion.div>
      </main>
    </div>
  );
};

export default RefundPolicy;
